<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import {
  AdminFilterBar,
  AdminDataTable,
  AdminDrawer,
  AdminStatusTag,
  RrInput,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,

  RrRadio,
  RrSwitch
} from '@xjlv2/admin-layout'
import type { TableColumnType } from '@xjlv2/admin-layout'
import { Copy, Plus, Search, ShieldCheck, Trash2, X } from '@lucide/vue'
import type { LexiconCategory, LexiconResult, ReviewPolicy, SavePolicyInput } from './types'
import {
  LEXICON_CATEGORY_OPTIONS,
  POLICY_STATUS_OPTIONS,
  REVIEW_SERVICE_OPTIONS,
  lexiconCategoryLabel,
  lexiconResultLabel,
  policyStatusLabel,
  reviewServiceLabel
} from './types'
import { lexiconResultClass } from './format'
import {
  createPolicy,
  deletePolicy,
  duplicatePolicy,
  listLexiconsByCategory,
  listPolicies,
  updatePolicy
} from './mock'
import { usePaged } from './usePaged'

const props = defineProps<{
  canManage: boolean
}>()

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
}>()

const loading = ref(false)
const submitting = ref(false)

// —— 策略列表 ——
const policies = ref<ReviewPolicy[]>([])
const keyword = ref('')
const listPaged = usePaged(() => policies.value, 10)

const columns: TableColumnType<ReviewPolicy>[] = [
  { key: 'id', title: '策略ID', width: 100 },
  { key: 'name', title: '策略名称', minWidth: 150 },
  { key: 'scene', title: '适用场景', minWidth: 150 },
  { key: 'service', title: '审核服务', width: 120 },
  { key: 'linkedCount', title: '关联词库', width: 100 },
  { key: 'status', title: '状态', width: 100, align: 'center' },
  { key: 'updatedAt', title: '修改时间', width: 180 },
  { key: 'actions', title: '操作', width: 180, align: 'right', fixed: 'right' }
]

function getResultTone(result: LexiconResult) {
  const r = result as string
  if (r === 'REJECT') return 'danger'
  if (r === 'REVIEW') return 'warning'
  if (r === 'PASS') return 'success'
  return 'neutral'
}


async function refreshPolicies() {
  loading.value = true
  emit('error', '')
  try {
    policies.value = await listPolicies(keyword.value)
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '策略加载失败')
  } finally {
    loading.value = false
  }
}

// —— 新建 / 编辑策略（配置抽屉）——
interface LexiconGroup {
  category: LexiconCategory
  items: Array<{ id: number; name: string; result: LexiconResult; termCount: number }>
}

const showDialog = ref(false)
const dialogMode = ref<'create' | 'edit'>('create')
const editingPolicy = ref<ReviewPolicy | null>(null)
const groups = ref<LexiconGroup[]>([])
const policyForm = ref<SavePolicyInput>({ name: '', scene: '', service: 'TEXT', linkedLexiconIds: [], status: 'ACTIVE' })

const linkedCount = computed(() => policyForm.value.linkedLexiconIds.length)

function groupItems(category: string) {
  return groups.value.find((group) => group.category === category)?.items ?? []
}

function groupAllSelected(category: string) {
  const items = groupItems(category)
  return items.length > 0 && items.every((item) => policyForm.value.linkedLexiconIds.includes(item.id))
}

async function openCreateDialog() {
  dialogMode.value = 'create'
  editingPolicy.value = null
  policyForm.value = { name: '', scene: '', service: 'TEXT', linkedLexiconIds: [], status: 'ACTIVE' }
  showDialog.value = true
  void loadGroups()
}

async function openEditDialog(policy: ReviewPolicy) {
  dialogMode.value = 'edit'
  editingPolicy.value = policy
  policyForm.value = {
    name: policy.name,
    scene: policy.scene,
    service: policy.service,
    linkedLexiconIds: [...policy.linkedLexiconIds],
    status: policy.status
  }
  showDialog.value = true
  void loadGroups()
}

async function loadGroups() {
  try {
    const data = await listLexiconsByCategory()
    groups.value = data.map((group) => ({
      category: group.category,
      items: group.items.map((item) => ({ id: item.id, name: item.name, result: item.result, termCount: item.termCount }))
    }))
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '关联词库加载失败')
  }
}

function toggleLexicon(id: number, event?: Event | boolean) {
  const index = policyForm.value.linkedLexiconIds.indexOf(id)
  if (index >= 0) {
    policyForm.value.linkedLexiconIds.splice(index, 1)
  } else {
    policyForm.value.linkedLexiconIds.push(id)
  }
}

function toggleGroup(category: string, event: Event | boolean) {
  const isChecked = typeof event === 'boolean' ? event : (event.target as HTMLInputElement).checked
  const ids = groupItems(category).map((item) => item.id)
  if (isChecked) {
    const set = new Set(policyForm.value.linkedLexiconIds)
    ids.forEach((id) => set.add(id))
    policyForm.value.linkedLexiconIds = [...set]
  } else {
    policyForm.value.linkedLexiconIds = policyForm.value.linkedLexiconIds.filter((id) => !ids.includes(id))
  }
}

async function submitPolicy() {
  if (!policyForm.value.name.trim()) {
    emit('error', '请输入策略名称。')
    return
  }
  if (!policyForm.value.scene.trim()) {
    emit('error', '请输入适用场景。')
    return
  }
  if (policyForm.value.status === 'ACTIVE' && !linkedCount.value) {
    emit('error', '生效中的策略至少需关联一个词库。')
    return
  }
  submitting.value = true
  try {
    if (dialogMode.value === 'create') {
      await createPolicy(policyForm.value)
    } else if (editingPolicy.value) {
      await updatePolicy(editingPolicy.value.id, policyForm.value)
    }
    showDialog.value = false
    emit('notice', dialogMode.value === 'create' ? '策略已创建。' : '策略配置已保存。')
    await refreshPolicies()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '策略保存失败')
  } finally {
    submitting.value = false
  }
}

async function copyPolicy(policy: ReviewPolicy) {
  loading.value = true
  try {
    const record = await duplicatePolicy(policy.id)
    emit('notice', `已复制生成新策略「${record.name}」。`)
    await refreshPolicies()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '策略复制失败')
  } finally {
    loading.value = false
  }
}

async function removePolicy(policy: ReviewPolicy) {
  if (!window.confirm(`确定删除策略「${policy.name}」吗？`)) return
  loading.value = true
  try {
    await deletePolicy(policy.id)
    emit('notice', '策略已删除。')
    await refreshPolicies()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '策略删除失败')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refreshPolicies()
})

defineExpose({ refresh: refreshPolicies })
</script>

<template>
  <section class="panel span management-list content-review-page">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Content Review · Policy</p>
        <h2>策略管理</h2>
        <small>配置内容审核策略，关联词库后生效于指定场景</small>
      </div>
    </div>

    <AdminFilterBar :loading="loading" @submit="refreshPolicies" style="margin-bottom: 16px;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 14px; color: var(--rr-color-text-secondary); white-space: nowrap;">策略名称 / ID</span>
        <RrInput v-model:value="keyword" placeholder="输入策略名称或 ID 进行搜索" @keyup.enter="refreshPolicies" style="width: 240px;" />
      </div>
      <template #actions>
        <RrButton :disabled="loading" @click="refreshPolicies">
          <Search :size="16" style="margin-right: 4px;" />查询
        </RrButton>
        <RrButton type="primary" :disabled="!canManage || loading" @click="openCreateDialog">
          <Plus :size="16" style="margin-right: 4px;" />新建策略
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="listPaged.paged.value"
      :loading="loading"
      :current="listPaged.page.value"
      :page-size="listPaged.pageSize.value"
      :total="listPaged.total.value"
      @update:current="listPaged.setPage"
      @update:page-size="(size) => (listPaged.pageSize.value = size)"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'id'">
          <strong>{{ record.id }}</strong>
        </template>
        <template v-else-if="column.key === 'name'">
          {{ record.name }}
        </template>
        <template v-else-if="column.key === 'scene'">
          {{ record.scene }}
        </template>
        <template v-else-if="column.key === 'service'">
          {{ reviewServiceLabel(record.service) }}
        </template>
        <template v-else-if="column.key === 'linkedCount'">
          {{ record.linkedLexiconIds.length }} 个
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag :text="policyStatusLabel(record.status)" :tone="record.status === 'ACTIVE' ? 'success' : 'neutral'" />
        </template>
        <template v-else-if="column.key === 'updatedAt'">
          {{ record.updatedAt }}
        </template>
        <template v-else-if="column.key === 'actions'">
          <span class="row-actions compact-actions">
            <RrButton type="text" :disabled="!canManage || loading" @click="openEditDialog(record)">编辑</RrButton>
            <RrButton type="text" :disabled="!canManage || loading" @click="copyPolicy(record)">复制</RrButton>
            <RrButton type="text" status="danger" :disabled="!canManage || loading" @click="removePolicy(record)">删除</RrButton>
          </span>
        </template>
      </template>
    </AdminDataTable>

    <AdminDrawer v-model:open="showDialog" :title="dialogMode === 'create' ? '新建策略' : '编辑策略'" size="500px">
      <RrForm :model="policyForm" :disabled="submitting" layout="vertical">
        <div style="font-weight: 500; font-size: 16px; margin-bottom: 16px;">基本信息</div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
          <RrFormItem label="策略名称" required>
            <RrInput v-model:value="policyForm.name" maxlength="32" placeholder="请输入策略名称" />
          </RrFormItem>
          <RrFormItem label="适用场景" required>
            <RrInput v-model:value="policyForm.scene" maxlength="32" placeholder="如：直播弹幕发言" />
          </RrFormItem>
        </div>

        <RrFormItem label="审核服务">
          <div style="display: flex; gap: 16px;">
            <RrRadio v-for="option in REVIEW_SERVICE_OPTIONS" :key="option.value" :value="option.value" :checked="policyForm.service === option.value" @change="policyForm.service = option.value">{{ option.label }}</RrRadio>
          </div>
        </RrFormItem>

        <RrFormItem label="生效状态">
          <div style="display: flex; align-items: center; gap: 8px;">
            <RrSwitch :checked="policyForm.status === 'ACTIVE'" @update:checked="policyForm.status = $event ? 'ACTIVE' : 'DISABLED'" />
            <span style="font-size: 13px; color: var(--rr-color-text-secondary);">{{ POLICY_STATUS_OPTIONS.find((option) => option.value === policyForm.status)?.label }}</span>
          </div>
        </RrFormItem>

        <div style="font-weight: 500; font-size: 16px; margin: 24px 0 8px;">
          关联词库 <span style="font-size: 13px; font-weight: normal; color: var(--rr-color-text-secondary); margin-left: 8px;">已选 {{ linkedCount }} 个</span>
        </div>
        <p style="font-size: 13px; color: var(--rr-color-text-secondary); margin-bottom: 16px;">按词库分类勾选需要在本策略中生效的敏感词库。</p>

        <div v-for="option in LEXICON_CATEGORY_OPTIONS" :key="option.value" style="margin-bottom: 16px; border: 1px solid var(--rr-color-border, #ebeef5); border-radius: 6px; padding: 12px;">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
            <RrCheckbox :checked="groupAllSelected(option.value)" @change="toggleGroup(option.value, $event)">
              <strong>{{ lexiconCategoryLabel(option.value) }}</strong>
            </RrCheckbox>
            <span style="font-size: 13px; color: var(--rr-color-text-secondary);">{{ groupItems(option.value).length }} 个词库</span>
          </div>

          <div v-if="groupItems(option.value).length" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; background: var(--rr-color-fill-lighter, #f8f9fa); padding: 12px; border-radius: 4px;">
            <div v-for="item in groupItems(option.value)" :key="item.id" style="display: flex; align-items: center; gap: 8px;">
              <RrCheckbox :checked="policyForm.linkedLexiconIds.includes(item.id)" @change="toggleLexicon(item.id, $event)">
                <span style="margin-right: 4px;">{{ item.name }}</span>
              </RrCheckbox>
              <AdminStatusTag :text="lexiconResultLabel(item.result)" :tone="getResultTone(item.result)" size="small" />
              <small style="color: var(--rr-color-text-secondary); margin-left: auto;">{{ item.termCount }} 词条</small>
            </div>
          </div>
          <p v-else style="font-size: 13px; color: var(--rr-color-text-secondary); padding: 8px 12px; background: var(--rr-color-fill-lighter, #f8f9fa); border-radius: 4px; margin: 0;">{{ lexiconCategoryLabel(option.value) }}分类下暂无词库</p>
        </div>
      </RrForm>

      <template #footer>
        <div style="display: flex; justify-content: flex-end; gap: 12px; width: 100%;">
          <RrButton :disabled="submitting" @click="showDialog = false">取消</RrButton>
          <RrButton type="primary" :loading="submitting" :disabled="submitting || !policyForm.name || !policyForm.scene" @click="submitPolicy">确定</RrButton>
        </div>
      </template>
    </AdminDrawer>
  </section>
</template>
